/**
 * Bootstrap analytics + error monitoring without any new npm dependency.
 *
 * Plausible — privacy-friendly, cookie-free, GDPR-compliant analytics.
 *   Set REACT_APP_PLAUSIBLE_DOMAIN to your registered domain
 *   (e.g. "lovinahome.com") to enable. We load their `script.js`
 *   asynchronously — no impact on initial paint.
 *
 * Sentry — error/perf monitoring via their CDN loader (no SDK install
 *   so we don't bloat bundle size). Set REACT_APP_SENTRY_DSN to enable.
 *   The loader auto-fetches the SDK only on errors, then replays them.
 *
 * Both are opt-in via env var — if the env var is missing, nothing
 * loads, nothing breaks.
 */
function loadScript(src, attrs = {}) {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`script[src="${src}"]`)) return; // idempotent
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  s.defer = true;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

export function initAnalytics() {
  const plausibleDomain = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
  if (plausibleDomain) {
    loadScript('https://plausible.io/js/script.js', {
      'data-domain': plausibleDomain,
    });
  }

  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
  const sentryLoader = process.env.REACT_APP_SENTRY_LOADER_URL;
  if (sentryDsn && sentryLoader) {
    // Sentry loader URL looks like:
    //   https://js.sentry-cdn.com/<publicKey>.min.js
    // It auto-initialises with the DSN baked into the loader, then
    // proxies any window.Sentry.* calls.
    loadScript(sentryLoader, {});
    if (typeof window !== 'undefined') {
      window.SENTRY_INITIAL_DSN = sentryDsn;
    }
  }
}
